import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { addressChannels, addressTips } from '@/mocks/homeData';

const statusMap: Record<string, { label: string; badge: string; dot: string }> = {
  online: { label: '可用', badge: 'border-accent-200 bg-accent-100 text-accent-800', dot: 'bg-accent-500' },
  backup: { label: '备用', badge: 'border-secondary-200 bg-secondary-100 text-secondary-900', dot: 'bg-secondary-500' },
  maintenance: { label: '维护中', badge: 'border-background-300 bg-background-200 text-foreground-600', dot: 'bg-foreground-400' },
};

export default function AddressList() {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (url: string, id: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      // clipboard unavailable in some sandboxes, silently ignore
    }
    setCopied(id);
    window.setTimeout(() => setCopied((cur) => (cur === id ? null : cur)), 1600);
  };

  return (
    <section id="address" className="w-full scroll-mt-24 bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-label tracking-[0.2em] text-primary-600">LATEST LINKS</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">最新可用地址</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                线路状态、实时延迟一目了然，按“推荐 → 加速 → 备用”的顺序尝试，基本都能顺利进场。
              </p>
            </div>
            <span className="inline-flex items-center gap-2 self-start rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs text-foreground-600 md:self-auto">
              <i className="ri-refresh-line text-accent-600" />
              自动校验 · 每 2 小时一轮
            </span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-3 lg:col-span-2">
            {addressChannels.map((channel, index) => {
              const meta = statusMap[channel.status] ?? statusMap.online;
              const disabled = channel.status === 'maintenance';
              return (
                <Reveal key={channel.id} delay={index * 60}>
                  <article className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:bg-background-100 md:flex-row md:items-center md:justify-between md:p-5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${meta.badge}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-xs text-foreground-600">{channel.tag}</span>
                        <h3 className="font-heading text-base font-bold text-foreground-950">{channel.name}</h3>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{channel.desc}</p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2 md:gap-3">
                      <span className="hidden text-xs text-foreground-500 sm:inline">
                        {disabled ? '—' : `${channel.latency}ms`}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(channel.url, channel.id)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                      >
                        <i className={copied === channel.id ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'} />
                        {copied === channel.id ? '已复制' : '复制地址'}
                      </button>
                      <a
                        href="#address"
                        aria-disabled={disabled}
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-xs font-medium transition-colors ${
                          disabled
                            ? 'pointer-events-none bg-background-200 text-foreground-400'
                            : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        }`}
                      >
                        <i className="ri-arrow-right-up-line" />
                        {disabled ? '暂不可用' : '立即进入'}
                      </a>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={120}>
            <aside className="flex flex-col gap-4 lg:sticky lg:top-32">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <div className="h-40 w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%20flat%20illustration%20of%20a%20friendly%20compass%20and%20route%20map%20with%20warm%20orange%20and%20amber%20tones%2C%20minimal%20geometric%20shapes%2C%20clean%20cream%20background%2C%20navigation%20concept%2C%20editorial%20vector%20style%2C%20high%20detail&width=800&height=800&seq=gcg-guide-01&orientation=squarish"
                    alt="91吃瓜 入口通道使用说明"
                    title="91吃瓜 最新地址 使用说明"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">通道使用小贴士</h3>
                  <ul className="mt-3 flex flex-col gap-3">
                    {addressTips.map((tip) => (
                      <li key={tip} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-600">
                        <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-primary-800">
                  <i className="ri-alert-line" />
                  认准官方通道
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本页所有入口均来自官方公布渠道。请勿轻信群消息或弹窗广告中的来源不明链接。
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}